// The match renderer (WebGL2 through three.js; docs/platform/ClientShell.md "Rendering",
// docs/platform/RenderFidelity.md "Render passes"). The passes of the original's scene script
// (CommonData/Scripts/SceneScript.xss) in order:
// - `DamagePass` (damage.ts);
// - `DRP_TransBG`: the skybox into the half-size background surface, then
//   `Pass_PP_BlurV`/`Pass_PP_BlurH` (passes.ts);
// - `FishTankPass` into the main colour (cleared to 0): the blurred background copied over the
//   screen (`BG_Copy_Layer`), then "FishTankBack";
// - `WaterReflectionPassBack` (main colour → reflection), `WaterPassBack`: the back water plane;
// - `FWDP_Transparent` (depth cleared): land BG+MID, objects (worms and every BaseMesh the sim
//   reports with a mesh instance, posed with the instance's anim states), particles, land FG,
//   the GUI meshes (`PPL_GUIGAMEFG`);
// - `WaterReflectionPassFront`, `WaterPassFront`: the front water plane, the front fish tank;
// - `PostProcessA` into `AASurfaceInputCol` (the main colour, `ParticlesAdditive`), `AAPass`
//   (FXAA) back into the main colour;
// - bloom and `EffectsPass` (vignette) onto the screen.
// Every target holds the raw colour values as the original's GL surfaces do: textures are
// sampled without sRGB decoding and nothing is encoded on output, so blending happens on the
// stored values exactly like the original's.
// The game camera is the one of `GameApp::SetupCameras` 0xd0c1f0 (perspective, horizontal
// half-extent 0.1821251 at distance 1, near 20, far 100000) posed by the client port of
// `CameraMan` (camera.ts) from the sim's camera views; it draws every layer here, the skybox
// included (see `camera`). Per-player view state is not lockstep state (ARCHITECTURE).
import * as THREE from 'three';
import { Background } from './background.ts';
import { Buildings } from './buildings.ts';
import { LandLayer } from './land.ts';
import { findMesh, instantiate, loadTemplate, poseLayers, poseSteps, type AnimEval, type AnimLayer, type MeshInstance } from './meshes.ts';
import { Curve } from '../../tools/viewer/wanim.js';
import { Particles } from './particles.ts';
import { RopeLayer } from './ropes.ts';
import { BG_BLUR_SIZE, DEFAULT_POST, PostChain, type PostTheme } from './passes.ts';
import { PostEffects } from './postfx.ts';
import { CameraMan, CAM_GADGET_EFFECT, CAM_SUDDEN_DEATH, FRUSTUM_X } from './camera.ts';
import { decodeAnims, decodeParticles, type AnimStep, type AnimView, type FrameView, type MeshView, type PoseInst, type SceneView, type WormPoses, type WormView } from '../sim/view.ts';
import { TracerLines } from './tracers.ts';
import { GunEffects } from './gunfx.ts';

/** `created`: the `XAnimInstance` creation step the instance was made for (-1: none yet).
 * `layer`: its render layer (`SceneIdEnum`), `placed`: the instance and layer `place` set up. */
type Obj = { inst: MeshInstance | null; loading: boolean; key: string; seen: number; created: number; layer: number; placed: { inst: MeshInstance; layer: number } | null };
/** A mesh attached to a worm (held gadget, gadget hat, death plunger; keyed by its resource name). */
type Gadget = { key: string; inst: MeshInstance | null; created: number };

/** Unapplied steps kept per mesh instance while its template loads (a minute of steps). */
const MAX_QUEUED_STEPS = 3600;

/** The render layers a `BaseMesh` is created on (`g_pLayerNames` index = `SceneIdEnum`, the frame
 * view's mesh `layer`); every other one (GameMID, 3) draws with the objects. */
const LAYER_WIND = 0, LAYER_GAME_BG = 1, LAYER_GAME_MID_BEHIND_GUI = 2, LAYER_GAME_MID = 3, LAYER_GAME_FG = 4;

/** Presented frames between the logic step that creates a post-processing effect
 * (`CreateAndPlayExplosionEffect`, the level fade-in, sudden death) and the first update that
 * plays it. Measured on hb-A's bazooka explosion (effects created at tick 3641; frames
 * 3640-3648 every 2 ticks): lag 0 puts the flash two frames early (3642: +31 mean against the
 * original), lag 2 one frame late (3646: +29), lag 1 matches every frame (within 1.8).
 * animals-A 3500 (the Old Lady's explosion at 3500): 6.93 → 3.03 mean abs. */
const POST_LAG = 1;

/** The zoom (pixels per world unit) at a camera distance `z` for a `width`-pixel target. */
function zoomAt(width: number, z: number): number {
  return width / (2 * Math.max(1, z) * FRUSTUM_X);
}

export class Renderer {
  gl: THREE.WebGLRenderer;
  canvas: HTMLCanvasElement;
  /** The game camera. It also draws the skybox layer: `PostProcessor::__CreateGraphicalAssets`
   * 0xcff1b0 sets `DRL_RenderGeometry_GameBG` to the game camera after `GameApp::SetupCameras`
   * gave it `m_BackgroundCamera`, so `CameraMan`'s background camera pose reaches no layer. */
  camera = new THREE.PerspectiveCamera(20, 16 / 9, 20, 100000);
  /** The client half of the game camera (camera.ts). */
  cameraMan = new CameraMan();
  /** Called after each sim step's camera view with its tick (the HUD's WormHUD objects run
   * their step there: `WormHUDPanel::UpdateTransitionState`'s camera call, hud.ts). */
  onCameraStep: ((tick: number) => void) | null = null;
  /** `DRL_RenderGeometry_GameBG`. */
  private skyScene = new THREE.Scene();
  /** `FishTankPass:FishTank`. */
  private fishTankScene = new THREE.Scene();
  /** `WaterPassBack:DRL_WaterLayer`. */
  private waterBackScene = new THREE.Scene();
  private backScene = new THREE.Scene();
  private objScene = new THREE.Scene();
  /** The meshes of `WS_RenderGeometry_GameMID_Behind_GUI` (the vehicles' damage meshes), in the
   * objects' pass before `PPL_GUIGAMEBG`. */
  private behindGui = new THREE.Group();
  private frontScene = new THREE.Scene();
  /** `WaterPassFront`: the front water plane and `DRL_FishTank`. */
  private waterFrontScene = new THREE.Scene();
  /** `PostProcessA:ParticlesAdditive`. */
  private additiveScene = new THREE.Scene();
  land = new LandLayer();
  /** `BuildingMan`'s graphical half and the building fades, the girder list, the soil flag
   * (buildings.ts), from the sim steps' landscape records. */
  private buildings = new Buildings();
  background = new Background();
  particles = new Particles();
  /** `NinjaRopeGraphicsMan`'s rope strips and grapples (ropes.ts), in the objects' layer. */
  private ropes = new RopeLayer();
  private post = new PostChain();
  /** `PostProcessor`'s effect animators and vignette (postfx.ts). */
  private postFx = new PostEffects(DEFAULT_POST.vignette);
  /** The tracer lines of the presented step (tracers.ts), in the GameMID layer. */
  private tracers = new TracerLines();
  /** The gadgets' muzzle fire and cartridge meshes (gunfx.ts), in the GameMID layer. */
  private gunFx = new GunEffects(this.objScene);
  /** The post-processing events of the stepped views waiting for their frame (`POST_LAG`). */
  private postQueue: { tick: number; kind: number; args: number[] }[] = [];
  /** The theme values the vignette default was last taken from. */
  private postTheme: PostTheme | null = null;
  private waterLevel = -2048;
  /** The frame view's tick: the original's presented-frame counter (1/60 s each). */
  private tick = 0;
  private objects = new Map<number, Obj>();
  /** Worm object id -> its held gadget mesh (`WormPoser::m_GadgetInst`). */
  private gadgets = new Map<number, Gadget>();
  /** Worm object id -> its persistent gadget hat (the head attachment `WormPoserCore::
   * PushPersistentGadgetHat` made). */
  private hats = new Map<number, Gadget>();
  /** Worm object id -> its death plunger (`WormPoser::m_DetonatorInst`). */
  private detonators = new Map<number, Gadget>();
  /** Mesh instance key (`w<worm>:<slot>`: 0 the worm mesh, 1 the gadget, 2 the hat, 3 the
   * plunger; `m<mesh>`) -> the `XAnimInstance` creation step of the sim's instance and its
   * states of every step not evaluated yet (the anim views, `feedAnims`). */
  private animFeed = new Map<string, { created: number; evals: AnimEval[] }>();
  /** GUI-pass meshes by key (`hudMesh`). */
  private hudMeshes = new Map<string, Gadget>();
  private hudScene = new THREE.Scene();
  /** `FWDP_Transparent:PPL_GUIGAMEBG`: GUI meshes drawn after `WS_RenderGeometry_GameMID_Behind_GUI`
   * and before `WS_RenderGeometry_GameMID` (SceneScript.xss), i.e. behind the worms
   * (`WormHUDPowerBar`, game/widgets.ts). */
  private hudBgScene = new THREE.Scene();
  private hudCamera = new THREE.OrthographicCamera(-960, 960, 540, -540, -1000, 1000);
  private frameNo = 0;
  theme: string | null = null;
  /** Camera centre (world units) and zoom (pixels per world unit on the z = 0 plane) of the last
   * pushed game camera (`CameraMan` pose). */
  cam = { x: 0, y: 0, zoom: 1 };
  width = 1;
  height = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    // No MSAA: the original renders into single-sampled surfaces.
    this.gl = new THREE.WebGLRenderer({ canvas, antialias: false, preserveDrawingBuffer: true, powerPreference: 'high-performance' });
    this.gl.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.gl.autoClear = false;
    this.backScene.add(this.land.back);
    this.frontScene.add(this.land.front);
    this.skyScene.add(this.background.sky);
    this.fishTankScene.add(this.background.fishTankBack);
    this.waterBackScene.add(this.background.water.back);
    this.waterFrontScene.add(this.background.water.front, this.background.fishTankFront);
    this.background.water.front.renderOrder = -1;
    // The particle layers in their passes (particles.ts): ParticlesBackground with the skybox
    // (blurred with it), WS_RenderGeometry_Wind before the BG tier, GameBG between the BG and MID
    // tiers, ParticlesBack after MID, GameMID_Behind_GUI before and GameMID after the objects,
    // ParticlesFront after them, GameFG after the FG tier, ParticlesWaterFront between the front
    // water and the fish tank, ParticlesAdditive after the front water pass.
    const g = this.particles.groups;
    // BaseMesh::QueryNodeTransform for the particle locators: the node's world matrix as the
    // object was last posed (syncWorm/syncMesh update it before the steps' particles apply).
    this.particles.locator = (id, name) => this.objects.get(id)?.inst?.root.getObjectByName(name)?.matrixWorld ?? null;
    this.skyScene.add(g.background);
    this.backScene.add(g.wind, g.gameBG, g.back);
    this.objScene.add(this.behindGui, g.gameMIDBehind, g.gameMID, g.front, this.ropes.group);
    this.ropes.setRenderer(this.gl);
    this.objScene.add(this.tracers.mesh);
    this.frontScene.add(g.gameFG);
    this.waterFrontScene.add(g.waterFront);
    this.additiveScene.add(g.additive);
    Object.assign(this.particles.order, { background: 1e6, wind: 0, gameBG: 150, back: 300, gameMIDBehind: -1e6, gameMID: 1e6, front: 2e6, gameFG: 700, waterFront: -0.5, additive: 0 });
    this.resize();
  }

  resize(): void {
    const r = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, Math.round(r.width));
    this.height = Math.max(1, Math.round(r.height));
    this.gl.setPixelRatio(1);
    this.gl.setSize(this.width, this.height, false);
    this.cameraMan.screenW = this.width;
    this.cameraMan.aspect = this.width / this.height;
  }

  /** The landscape and the theme background of the match (once per match). */
  async setScene(scene: SceneView, theme: string | null): Promise<void> {
    this.theme = theme;
    this.ropes.theme = theme;
    const net = this.buildings.net;
    this.buildings = new Buildings();
    this.buildings.net = net;
    this.land.fades.value = this.buildings.fades;
    const rim = this.land.damage.load(theme).catch(e => console.warn('damage rim', theme, e));
    const textures = this.land.loadTextures(theme).catch(e => console.warn('soil, girders', theme, e));
    await Promise.all([this.land.build(scene, this.gl), this.background.load(theme), rim, textures]);
  }

  /** The local teams of a network match (`NetSetup::IsLocalTeam`/`IsLocalWorm` of the building
   * rules, `NetGame::IsInPlayRivalLocal` of the camera); null offline. */
  /** The team is played on this machine (`NetSetup::IsLocalTeam`; offline every team). */
  isLocalTeam(team: number): boolean {
    return !this.buildings.net.running || this.buildings.net.localTeam(team);
  }

  setLocalTeams(local: ((team: number) => boolean) | null): void {
    this.buildings.net = { running: !!local, localTeam: local ?? (() => false) };
    this.cameraMan.localTeam = local;
  }

  setLandMask(bytes: ArrayBuffer): void {
    this.land.setMask(bytes);
  }

  /** The bake's state grid (sim::view::land(2)): the flags the land bake writes into the tiles'
   * alpha (land.ts, bake.ts). */
  setBakeState(bytes: ArrayBuffer): void {
    this.land.setBakeState(bytes);
  }

  worldToScreen(x: number, y: number): { x: number; y: number } {
    return { x: (x - this.cam.x) * this.cam.zoom + this.width / 2, y: this.height / 2 - (y - this.cam.y) * this.cam.zoom };
  }

  /** Brings the scene to a frame view (camera steps, instances created/removed, poses). The
   * view's camera views are consumed (the same view may be drawn again). */
  update(v: FrameView, _dt: number): void {
    this.frameNo++;
    // Every step's camera view in order. The post-processing animators advance once per step
    // (`PostProcessor::Update` runs every presented frame, a sim step in the harness's fixed
    // mode), fast-forward included: an effect plays at its step, not when a frame is first drawn.
    // The original shows an effect `POST_LAG` frames after the logic created it.
    for (const c of v.cams ?? []) {
      this.land.damage.queue(this.cameraMan.step(c));
      const tick = new DataView(c.buffer, c.byteOffset, c.byteLength).getUint32(4, true);
      this.onCameraStep?.(tick);
      // TurnLogic::ApplySuddenDeathEffect's post-processing, the explosion effects
      // (CreateAndPlayExplosionEffect, the level fade-in) and the laser strike's own effects
      // (postfx.ts).
      for (const e of this.cameraMan.postEvents.splice(0)) this.postQueue.push({ tick, ...e });
      while (this.postQueue.length && this.postQueue[0].tick + POST_LAG <= tick) {
        const e = this.postQueue.shift()!;
        if (e.kind === CAM_SUDDEN_DEATH) void this.postFx.suddenDeath();
        else if (e.kind === CAM_GADGET_EFFECT) this.postFx.gadget(e.args);
        else this.postFx.explosion(e.args);
      }
      this.postFx.update(tick);
    }
    v.cams = [];
    // The landscape records of every step: building fades, girders, soil (buildings.ts).
    for (const b of this.cameraMan.landscape.splice(0)) this.buildings.step(b);
    this.land.girders.set(this.buildings.girders);
    this.land.setState(this.buildings.soilEnabled);
    const p = this.cameraMan.pose();
    this.cam = { x: p.x, y: p.y, zoom: zoomAt(this.width, p.z) };
    this.waterLevel = v.waterLevel;
    this.tick = v.tick;
    // The skybox clip starts at the bake's end (the theme objects' creation).
    if (!this.background.skyStart && v.bakeTick) this.background.skyStart = v.bakeTick;
    this.land.setWater(v.waterLevel);
    // Every sim step's anim view, in order: the states each mesh instance is evaluated with. An
    // instance the newest view does not list is gone in the sim: its queue goes.
    const views = (v.anims ?? []).map(decodeAnims);
    v.anims = [];
    if (views.length) {
      const live = new Set<string>();
      for (const av of views) this.feedAnims(av, av === views[views.length - 1] ? live : null);
      for (const key of this.animFeed.keys()) if (!live.has(key)) this.animFeed.delete(key);
    }
    for (const w of v.worms) this.syncWorm(w, v.poses.get(w.id));
    for (const m of v.meshes) this.syncMesh(m);
    // Every sim step's particle view, in order (particles.ts), after the objects are posed: the
    // attached effects take their locators from them.
    for (const b of v.fx ?? []) this.particles.push(decodeParticles(b));
    v.fx = [];
    this.ropes.update(v.ropes ?? []);
    this.tracers.update(v.tick, v.events, this.width, this.height);
    this.gunFx.update(v.tick, v.events, w => this.gadgets.get(w)?.inst ?? null, m => this.objects.get(m)?.inst ?? null, this.theme);
    for (const [id, o] of this.objects) {
      if (o.seen !== this.frameNo) {
        o.inst?.root.removeFromParent();
        this.objects.delete(id);
        this.gadgets.delete(id);
        this.hats.delete(id);
        this.detonators.delete(id);
        for (const key of [`m${id}`, `w${id}:0`, `w${id}:1`, `w${id}:2`, `w${id}:3`]) this.animFeed.delete(key);
      }
    }
  }

  /** Queues one step's anim view: per mesh instance its states (a new creation step starts a new
   * queue: the sim made a new instance). A step whose states write nothing (no weight) or equal
   * the queued last step's is not queued: evaluating it changes nothing. `live` collects the
   * instance keys the view lists. */
  private feedAnims(av: AnimView, live: Set<string> | null): void {
    const put = (key: string, s: AnimStep): void => {
      live?.add(key);
      let f = this.animFeed.get(key);
      if (!f || f.created !== s.created) {
        f = { created: s.created, evals: [] };
        this.animFeed.set(key, f);
      }
      if (!s.claimed.length && !s.anims.some(a => a.weight !== 0)) return;
      const last = f.evals[f.evals.length - 1];
      const same = last && last.claimed.join('\u0000') === s.claimed.join('\u0000') && last.layers.length === s.anims.length
        && last.layers.every((l, i) => l.clip === s.anims[i].clip && l.time === s.anims[i].time && l.weight === s.anims[i].weight);
      if (same) return;
      f.evals.push({ layers: s.anims, claimed: s.claimed });
      if (f.evals.length > MAX_QUEUED_STEPS) f.evals.shift();
    };
    for (const [id, insts] of av.worms) insts.forEach((s, slot) => { if (s) put(`w${id}:${slot}`, s); });
    for (const [id, s] of av.meshes) put(`m${id}`, s);
  }

  /** Evaluates the queued steps of instance `key` on `holder.inst` (meshes.ts `poseSteps`). A
   * client instance made for another `XAnimInstance` of the sim (another creation step) is
   * replaced by a fresh one first: the original's new instance starts from its bind pose. */
  private evalSteps(key: string, holder: { inst: MeshInstance | null; created: number }): void {
    const f = this.animFeed.get(key);
    const old = holder.inst;
    if (!f || !old) return;
    if (holder.created !== f.created) {
      if (holder.created !== -1) {
        const fresh = instantiate(old.template);
        old.root.parent?.add(fresh.root);
        old.root.removeFromParent();
        holder.inst = fresh;
      }
      holder.created = f.created;
    }
    if (f.evals.length && holder.inst) {
      poseSteps(holder.inst, f.evals);
      f.evals = [];
    }
  }

  /** An instance of mesh `name` for sim object `id` on render layer `layer` (loads its template on
   * first use). */
  private obtain(id: number, name: string, source: string | null, layer = LAYER_GAME_MID): Obj {
    const key = `${source ?? ''}|${name}`;
    let o = this.objects.get(id);
    if (o && o.key !== key) {
      o.inst?.root.removeFromParent();
      o = undefined;
    }
    if (!o) {
      const obj: Obj = { inst: null, loading: true, key, seen: this.frameNo, created: -1, layer, placed: null };
      o = obj;
      this.objects.set(id, obj);
      const f = findMesh(name, source || null, this.theme);
      if (f) {
        void loadTemplate(f.glb, f.wanim).then(t => {
          obj.loading = false;
          if (!t || this.objects.get(id) !== obj) return;
          obj.inst = instantiate(t);
          this.place(obj, obj.layer);
        });
      } else obj.loading = false;
    }
    o.seen = this.frameNo;
    return o;
  }

  /** Puts an object's instance into the pass of its render layer, as SceneScript.xss orders
   * `FWDP_Transparent`: Wind, LandscapeBG, GameBG, LandscapeMID, ParticlesBack,
   * GameMID_Behind_GUI, PPL_GUIGAMEBG, GameMID, ParticlesFront, LandscapeFG, GameFG,
   * PPL_GUIGAMEFG. A layer's shapes draw before its particles (their `renderOrder`; the
   * particle layers' orders are in the constructor) and back to front among themselves
   * (`Sort(State,BackToFront)`: three.js's transparent sort by the bounding-sphere centres). The
   * order goes on the instance's drawables, not its groups: a group's order would rank its
   * subtree before every ungrouped order. Again after a new instance (`evalSteps`). */
  private place(o: Obj, layer: number): void {
    o.layer = layer;
    const inst = o.inst;
    if (!inst || (o.placed?.inst === inst && o.placed.layer === layer)) return;
    const [parent, order] = layer === LAYER_WIND ? [this.backScene, -0.5]
      : layer === LAYER_GAME_BG ? [this.backScene, 149]
      : layer === LAYER_GAME_MID_BEHIND_GUI ? [this.behindGui, -1e6 - 1]
      : layer === LAYER_GAME_FG ? [this.frontScene, 650]
      : [this.objScene, 0];
    if (inst.root.parent !== parent) parent.add(inst.root);
    inst.root.traverse(c => { if (!(c instanceof THREE.Group)) c.renderOrder = order; });
    o.placed = { inst, layer };
  }

  /** The worm mesh and its attachments as the sim's `WormPoser::UpdatePoserGraphical` 0xc11de0
   * left them (crates/sim/src/worm/worm_poser_graphical.rs; the frame view's poses): the worm
   * mesh's visibility, its clip states evaluated step by step (none drawn before the mesh
   * exists), its position, orientation and scale from the graphic walk (one logic tick behind
   * `m_Ld`, as the original draws), then the held gadget, the gadget hat and the death plunger. */
  private syncWorm(w: WormView, poses: WormPoses | undefined): void {
    const p = w.poser;
    if (!p) return;
    const o = this.obtain(w.id, p.mesh || 'Worm', null);
    this.evalSteps(`w${w.id}:0`, o);
    const inst = o.inst;
    if (!inst) return;
    const wp = poses?.worm ?? null;
    inst.root.visible = !!wp?.visible;
    const t = wp?.transform;
    if (t) {
      inst.root.position.set(t.pos[0], t.pos[1], t.pos[2]);
      inst.root.rotation.set(t.orientation[0], t.orientation[1], t.orientation[2]);
      inst.root.scale.set(t.scale[0], t.scale[1], Math.max(t.scale[2], 0.01));
    }
    this.syncAttached(this.gadgets, w.id, 1, inst, poses?.gadget ?? null);
    this.syncAttached(this.hats, w.id, 2, inst, poses?.hat ?? null);
    this.syncAttached(this.detonators, w.id, 3, inst, poses?.detonator ?? null);
    inst.root.updateMatrixWorld(true);
  }

  /** The mesh `name` attached to worm `id` in `map` (loaded on first use; a different name drops
   * the old one, '' drops it); null while it loads. */
  private attached(map: Map<number, Gadget>, id: number, name: string): Gadget | null {
    let g = map.get(id);
    if (g && g.key !== name) {
      g.inst?.root.removeFromParent();
      map.delete(id);
      g = undefined;
    }
    if (!name) return null;
    if (!g) {
      const entry: Gadget = { key: name, inst: null, created: -1 };
      g = entry;
      map.set(id, entry);
      const f = findMesh(name, null, this.theme);
      if (f) void loadTemplate(f.glb, f.wanim).then(t => { if (t && map.get(id) === entry) entry.inst = instantiate(t); });
    }
    return g;
  }

  /** A poser instance attached to the worm mesh (`XMeshInstance` slot 24 `AttachToNode`): loaded
   * by name, parented to its node, with the sim's visibility and every step's clip states
   * (`slot` of the anim views: 1 gadget, 2 hat, 3 plunger). */
  private syncAttached(map: Map<number, Gadget>, id: number, slot: number, worm: MeshInstance, pi: PoseInst | null): void {
    const g = this.attached(map, id, pi?.mesh ?? '');
    if (!g || !pi) return;
    this.evalSteps(`w${id}:${slot}`, g);
    const gi = g.inst;
    if (!gi) return;
    const node = worm.root.getObjectByName(pi.node) ?? worm.root;
    if (gi.root.parent !== node) node.add(gi.root);
    gi.root.visible = pi.visible;
  }

  /** A BaseMesh as its mesh instance draws it: only with an instance (`m_pMeshInst`) and the
   * instance's visibility, in the pass of its render layer (`place`); its `XAnimInstance` states
   * evaluated step by step (an existing hidden instance too: its fields persist like the
   * original's); position, orientation and scale as the graphic walk gave the instance
   * (`BaseMesh::m_InstanceTransform`, one logic tick behind `m_Ld`), the orientation as
   * `XResourceInstance::SetOrientation` 0x1186270 builds it (the root XTransform's rotate order
   * XYZ: row vectors x·Rx·Ry·Rz, X first, three.js order 'ZYX'). */
  private syncMesh(m: MeshView): void {
    if (!m.visible || !m.instance) {
      const o = this.objects.get(m.id);
      if (o) o.seen = this.frameNo;
      if (o?.inst) {
        if (m.instance) this.evalSteps(`m${m.id}`, o);
        if (o.inst) o.inst.root.visible = false;
      }
      return;
    }
    const o = this.obtain(m.id, m.mesh || m.resource, m.source || null, m.layer);
    this.evalSteps(`m${m.id}`, o);
    this.place(o, m.layer);
    const inst = o.inst;
    if (!inst) return;
    inst.root.visible = true;
    inst.root.position.set(m.pos[0], m.pos[1], m.pos[2]);
    inst.root.rotation.set(m.orientation[0], m.orientation[1], m.orientation[2], 'ZYX');
    inst.root.scale.set(m.scale[0], m.scale[1], Math.max(m.scale[2], 0.01));
    inst.root.updateMatrixWorld(true);
  }

  /** A mesh of the GUI pass (`FWDP_Transparent:PPL_GUIGAMEFG`, e.g. `WormHUDCurrentArrow`'s
   * `HU_TurnArrow_01`): mesh `name` of the match bundles under `key` at HUD design coordinates
   * (`hudX`, `hudY`) (origin at the screen centre, y up, one unit a pixel of the 1920x1080 design
   * scaled by min(w/1920, h/1080)), posed with `clip` at `time` and drawn unlit with its texture
   * multiplied by `tint` (RGB 0..1), as UI_Trans_Emiss_Tinted. Called every frame it is shown. */
  hudMesh(key: string, name: string, hudX: number, hudY: number, visible: boolean, clip: string | null, time: number, tint: [number, number, number]): void {
    this.hudWidget(key, name, false, hudX, hudY, 0, [1, 1, 1], visible, clip ? [{ clip, time, weight: 1 }] : [], tint, null);
  }

  /** A GUI mesh of the HUD widgets (game/widgets.ts): as `hudMesh`, in the GUI background layer
   * (`PPL_GUIGAMEBG`, behind the worms) when `bg`, rotated by `rotZ` (radians) and scaled, posed
   * with `layers`; `uvOffset` (null: none) is `BG_Trans_Emis_UVt`'s `UvoffsetColor.r`, the texture's
   * U shift (Root.BG.BG_Trans_Emis_UVt.Frag: uv + UvoffsetColor.rg). */
  hudWidget(key: string, name: string, bg: boolean, hudX: number, hudY: number, rotZ: number, scale: [number, number, number], visible: boolean,
    layers: AnimLayer[], tint: [number, number, number], uvOffset: number | null): void {
    let e = this.hudMeshes.get(key);
    if (!e || e.key !== name) {
      e?.inst?.root.removeFromParent();
      const entry: Gadget = { key: name, inst: null, created: -1 };
      e = entry;
      this.hudMeshes.set(key, entry);
      const f = findMesh(name, null, this.theme);
      if (f) void loadTemplate(f.glb, f.wanim).then(t => {
        if (!t || this.hudMeshes.get(key) !== entry) return;
        const inst = instantiate(t);
        inst.root.traverse(o => {
          if (!(o instanceof THREE.Mesh)) return;
          const src = o.material as THREE.Material & { map?: THREE.Texture | null };
          o.material = new THREE.MeshBasicMaterial({
            map: src.map ?? null, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide, vertexColors: src.vertexColors,
            blending: src.blending, blendSrc: src.blendSrc, blendDst: src.blendDst, alphaTest: src.alphaTest,
          });
        });
        entry.inst = inst;
        (bg ? this.hudBgScene : this.hudScene).add(inst.root);
      });
    }
    const inst = e.inst;
    if (!inst) return;
    inst.root.visible = visible;
    if (!visible) return;
    poseLayers(inst, layers);
    inst.root.traverse(o => {
      if (!(o instanceof THREE.Mesh)) return;
      const m = o.material as THREE.MeshBasicMaterial;
      m.color.setRGB(tint[0], tint[1], tint[2]);
      if (uvOffset !== null && m.map) { m.map.offset.set(uvOffset, 0); m.map.wrapS = THREE.RepeatWrapping; }
    });
    inst.root.position.set(hudX, hudY, 0);
    inst.root.rotation.set(0, 0, rotZ);
    inst.root.scale.set(scale[0], scale[1], scale[2]);
    inst.root.updateMatrixWorld(true);
  }

  /** The value `clip` gives the uniform attribute `path` ("Material|UniformC") of mesh `name` at
   * `time` (null before the mesh loaded or without that channel). */
  hudClipValue(name: string, clip: string, path: string, time: number): number | null {
    const t = this.hudMeshes.get(name)?.inst?.template;
    const c = t?.anim?.clips.find(x => x.name === clip);
    const ch = c?.channels.find(x => t!.anim!.attrs[x.attr].path === path);
    return ch ? new Curve(ch).evaluate(time) : null;
  }

  /** `WormHUDMan::WorldToHUD` 0xb2f2b0: the game camera's projection of (x, y, z) (y up, world
   * units) in the GUI pass's design space (origin at the screen centre, y up). It goes through
   * `XCamera::WorldToScreen` 0x123c250, which keeps the projection's first three columns
   * (`XMatrix43`) and divides x and y by the clip z, (d (f + n) - 2 f n) / (f - n) for the view
   * distance d (near n 20, far f 100000: about d - 40), not by d: points land d / clip z
   * (about 1.2 %) further from the screen centre than the scene draws them. hb-A 3420, reticle
   * at (-624.04, 45.00, -40): the original's (-366.996, 45.489), clip z 3476.398 (gdb). */
  worldToHud(x: number, y: number, z: number): { x: number; y: number } {
    this.poseCamera();
    const c = this.camera;
    const p = new THREE.Vector3(x, y, z);
    const d = -p.clone().applyMatrix4(c.matrixWorldInverse).z;
    const clipZ = (d * (c.far + c.near) - 2 * c.far * c.near) / (c.far - c.near);
    const v = p.project(c);
    const k = d / clipZ, hs = Math.min(this.width / 1920, this.height / 1080);
    return { x: (v.x * k * this.width) / (2 * hs), y: (v.y * k * this.height) / (2 * hs) };
  }

  /** Poses the game camera from the `CameraMan` pose (`GameApp::SetupCameras` frustum). */
  private poseCamera(): void {
    const aspect = this.width / this.height;
    const p = this.cameraMan.pose();
    const c = this.camera;
    c.aspect = aspect;
    c.fov = (2 * Math.atan(FRUSTUM_X / aspect) * 180) / Math.PI;
    c.position.set(p.x, p.y, p.z);
    c.up.set(0, 1, 0);
    c.lookAt(p.lookX, p.lookY, p.lookZ);
    c.updateProjectionMatrix();
    c.updateMatrixWorld();
  }

  render(): void {
    this.poseCamera();
    const c = this.camera;
    this.background.update(this.waterLevel, this.tick);
    this.particles.draw();
    const gl = this.gl;
    const post = this.post;
    post.resize(this.width, this.height);
    post.theme = this.background.post;
    if (this.background.vignette) post.setVignette(this.background.vignette);
    // ThemeMan::LoadResources: Vignette_Defaults of the theme's VignetteIntensity, BloomDefaults
    // of its bloom values.
    if (this.postTheme !== this.background.post) {
      this.postTheme = this.background.post;
      this.postFx.setThemeVignette(this.postTheme.vignette);
      this.postFx.setThemeBloom(this.postTheme.bloomBlurSize, this.postTheme.bloomIntensity, this.postTheme.bloomThreshold);
      this.postFx.refreshUniforms();
    }
    this.land.damage.flush(gl);
    gl.setClearColor(0x000000, 0);
    // DRP_TransBG: Clear Colour(0,0,0,0) Clear Depth; the skybox.
    gl.setRenderTarget(post.bg);
    gl.clear(true, true, false);
    gl.render(this.skyScene, c);
    post.blur(gl, post.bg, BG_BLUR_SIZE);
    // FishTankPass (Opaque, Clear Colour(0,0,0,0) Clear Depth): BG_Copy_Layer, then FishTank.
    gl.setRenderTarget(post.main);
    gl.clear(true, true, false);
    post.copy(gl, post.blurH.texture, post.main);
    gl.render(this.fishTankScene, c);
    // WaterReflectionPassBack, WaterPassBack.
    post.copy(gl, post.main.texture, post.reflection);
    this.background.water.update(this.waterLevel, c, post.reflection.texture, this.tick);
    gl.setRenderTarget(post.main);
    gl.render(this.waterBackScene, c);
    const hs = Math.min(this.width / 1920, this.height / 1080);
    const hc = this.hudCamera;
    hc.left = -this.width / (2 * hs);
    hc.right = this.width / (2 * hs);
    hc.top = this.height / (2 * hs);
    hc.bottom = -this.height / (2 * hs);
    hc.updateProjectionMatrix();
    // FWDP_Transparent (Clear Depth): land BG+MID with the Wind and GameBG layers, objects and
    // particles (GameMID_Behind_GUI, PPL_GUIGAMEBG, GameMID, ParticlesFront), land FG with the
    // GameFG layer, GUI meshes (PPL_GUIGAMEFG).
    for (const s of [this.backScene, this.objScene, this.frontScene]) {
      gl.clearDepth();
      if (s === this.objScene && this.hudBgScene.children.some(o => o.visible)) {
        const behind = new Set<THREE.Object3D>([this.behindGui, this.particles.groups.gameMIDBehind]), shown = s.children.map(o => o.visible);
        s.children.forEach(o => { o.visible = behind.has(o) && o.visible; });
        gl.render(s, c);
        gl.render(this.hudBgScene, hc);
        s.children.forEach((o, i) => { o.visible = !behind.has(o) && shown[i]; });
        gl.render(s, c);
        s.children.forEach((o, i) => { o.visible = shown[i]; });
      } else gl.render(s, c);
    }
    gl.render(this.hudScene, hc);
    // WaterReflectionPassFront, WaterPassFront (DRL_WaterLayer, then DRL_FishTank).
    post.copy(gl, post.main.texture, post.reflection);
    gl.setRenderTarget(post.main);
    gl.clearDepth();
    gl.render(this.waterFrontScene, c);
    // PostProcessA (kAA_Fxaa: AAInputCanvas, Clear Depth): CombineLayers (FinalPass, the main
    // colour as it is), ParticlesAdditive; AAPass: FXAA back into the main colour (DRC_Final).
    post.copy(gl, post.main.texture, post.aaInput);
    gl.render(this.additiveScene, c);
    post.antiAlias(gl);
    post.finish(gl, this.postFx.uniforms);
  }
}
